import { statusStyle } from './status.js'
import './components.css'

/**
 * The console's component library.
 *
 * Exists because the seven static pages had none: the SAIL palette was redeclared in all seven files, six
 * section-header patterns were rolled by hand, and ~316 lines of `<style>` were duplicated. That is the same
 * fault the M13 audit measured on Android (23 ad-hoc row helpers, four byte-identical) — so the web starts
 * with shared components rather than growing into the same problem.
 *
 * Every state a screen can be in is a component here, because CLAUDE.md rule 19 requires all of them to
 * render: loading, populated, empty, error, permission-denied. On the old pages an error was a bare red
 * sentence and "no data yet" looked identical to "the request failed".
 */

/* ---- Status ------------------------------------------------------------- */

/**
 * A status pill: colour **and** icon **and** label, never colour alone (rule 3).
 *
 * The icon is not decoration. A supervisor with deuteranopia cannot separate the amber and red pills, and
 * neither can anyone reading a laptop screen in a plant window; the glyph and the word are what actually
 * carry the state.
 */
export function StatusPill({ status, title }) {
  const s = statusStyle(status)
  return (
    <span className={`pill pill-${s.kind}`} title={title || (s.known ? undefined : `Unrecognised status: ${status}`)}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d={s.icon} fill="currentColor" /></svg>
      {s.label}
    </span>
  )
}

/* ---- Layout ------------------------------------------------------------- */

export function Card({ title, subtitle, actions, children, accent }) {
  return (
    <section className={`card${accent ? ` card-accent-${accent}` : ''}`}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p className="muted">{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

export function SectionHeader({ children, hint }) {
  // One definition, unlike the six the Android screens grew (audit M13). A heading is a heading.
  return (
    <div className="section-head">
      <h3>{children}</h3>
      {hint && <span className="muted">{hint}</span>}
    </div>
  )
}

/* ---- Metric tiles ------------------------------------------------------- */

/**
 * One headline number.
 *
 * `value` renders with tabular figures (rule 8) so a row of tiles does not jiggle as numbers refresh and a
 * column of them aligns. `unit` is separate from `value` so "94.2" and "%" can be sized differently without
 * string surgery.
 *
 * A null value renders an em dash, never `0`. Reporting zero for "no data yet" is the defect the reliability
 * report avoids by design — a crane with no failures showing `MTBF 0` reads as the worst machine in the plant
 * when it is the best.
 */
export function Metric({ label, value, unit, hint, kind, onClick, emphasis }) {
  const missing = value === null || value === undefined
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      className={`metric${kind ? ` metric-${kind}` : ''}${emphasis ? ' metric-lg' : ''}${onClick ? ' metric-click' : ''}`}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      <span className="metric-label">{label}</span>
      <span className="metric-value tnum">
        {missing ? <span className="metric-none" title="No data in this period">—</span> : value}
        {!missing && unit && <span className="metric-unit">{unit}</span>}
      </span>
      {hint && <span className="metric-hint">{hint}</span>}
    </Tag>
  )
}

export function MetricRow({ children }) {
  return <div className="metric-row">{children}</div>
}

/* ---- States (rule 19: every one of these renders) ----------------------- */

export function Loading({ rows = 4, label = 'Loading' }) {
  return (
    <div className="skeleton" role="status" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="skeleton-row" />)}
    </div>
  )
}

export function Empty({ headline, hint, action }) {
  return (
    <div className="state">
      <svg viewBox="0 0 24 24" className="state-icon" aria-hidden="true">
        <path fill="currentColor" d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm0 16H5V5h14v14zM7 12h10v2H7z" />
      </svg>
      <h3>{headline}</h3>
      {hint && <p className="muted">{hint}</p>}
      {action}
    </div>
  )
}

/**
 * An error, distinguished by *kind*.
 *
 * A 403 and a dropped connection need different words and different next steps. The M9 audit found one
 * message blaming the connection for a permission error, which sent a supervisor to restart their phone over
 * a right they did not have — so this reads the status and says which it is.
 */
export function ErrorState({ error, onRetry }) {
  const status = error?.status
  const denied = status === 403
  return (
    <div className={`state ${denied ? 'state-denied' : 'state-error'}`}>
      <svg viewBox="0 0 24 24" className="state-icon" aria-hidden="true">
        <path
          fill="currentColor"
          d={denied
            ? 'M18 8h-1V6a5 5 0 00-10 0v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zm-6 9a2 2 0 110-4 2 2 0 010 4zM9 8V6a3 3 0 016 0v2H9z'
            : 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'}
        />
      </svg>
      <h3>{denied ? 'Not permitted' : 'Could not load'}</h3>
      <p className="muted">{error?.message || 'Something went wrong.'}</p>
      {/* No retry offered for a 403: the request will fail identically, and a Retry button that cannot
          succeed is worse than none because it implies the problem is transient. */}
      {!denied && onRetry && <button className="btn" onClick={onRetry}>Try again</button>}
    </div>
  )
}

export function Banner({ kind = 'info', children, action }) {
  return (
    <div className={`banner banner-${kind}`}>
      <span>{children}</span>
      {action}
    </div>
  )
}

/* ---- Table -------------------------------------------------------------- */

/**
 * A dense data table, which is the thing a desk screen is *for*.
 *
 * Web-native rather than a stretched phone list: the Android app shows one crane per card because a phone
 * has room for one; a supervisor comparing fourteen cranes wants them in rows they can scan down a column.
 * Numeric columns are right-aligned with tabular figures so the digits line up.
 */
export function Table({ columns, rows, empty, onRowClick, keyOf }) {
  if (!rows?.length) return empty || <Empty headline="Nothing to show" />
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'num' : undefined} style={c.width ? { width: c.width } : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={keyOf ? keyOf(r) : i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={onRowClick ? 'row-link' : undefined}
              // Keyboard reachable when clickable: a plant PC is often driven without a mouse.
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter') onRowClick(r) } : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'num tnum' : undefined}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ---- Controls ----------------------------------------------------------- */

export function Button({ children, variant = 'primary', ...rest }) {
  return <button className={`btn btn-${variant}`} {...rest}>{children}</button>
}

export function Field({ label, hint, children, id }) {
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="muted field-hint">{hint}</span>}
    </label>
  )
}

/** Duration in minutes -> the unit a human would use. Minutes below an hour, then hours, then days. */
export function fmtMinutes(m) {
  if (m === null || m === undefined) return null
  if (m < 60) return `${Math.round(m)} min`
  if (m < 60 * 48) return `${(m / 60).toFixed(1)} h`
  return `${(m / 1440).toFixed(1)} d`
}

export function fmtPct(v) {
  return v === null || v === undefined ? null : v.toFixed(1)
}
